import { InputLabel, Select } from '@mui/material';
import { FormControl } from '@mui/material';
import { MenuItem } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getAccountingEntries,
  getAccountingEntryClasses,
  getAccountingEntryTypes,
  getAccountingNodes,
  getAccountingPeriods,
} from '../../../../../../lib/api/accounting';
import CustomButton from '../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions from '../../../../../../lib/designSystem/HeaderWithActions';
import MenuDialog from '../../../../../../lib/designSystem/MenuDialog';

const MONTHS = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

const YEARS = Array.from({ length: 10 }, (_, index) => 2025 - index);

import CustomAlert from '../../../../../../lib/designSystem/Alert';
import { hasAnyPermissions } from '../../../../../../lib/functions/utils';
import { AuthState } from '../../../../../../services/redux/authSlice';
import {
  AccountingEntry,
  AccountingEntryClass,
  AccountingNode,
  AccountingPeriod,
  DatesSearcherProps,
  EntryType,
} from '../../types';
import EditEntry from './components/EntryWithLines/EditEntry';
import AccountingEntriesTable from './components/Table';

const DatesSearcher = (props: DatesSearcherProps) => {
  const { setUp, year, month, setMonth, setYear } = props;
  const handleSearch = async () => {
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 1);
    await setUp(startDate, endDate);
  };

  return (
    <MenuDialog buttonLabel="Buscar" buttonColor="secondary">
      <div style={{ padding: 10 }}>
        <div style={{ padding: '10px 0px' }}>
          <FormControl fullWidth>
            <InputLabel>Mes</InputLabel>
            <Select
              label="Mes"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {MONTHS.map((month, index) => (
                <MenuItem key={index} value={index + 1}>
                  {month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div style={{ padding: '10px 0px' }}>
          <FormControl fullWidth>
            <InputLabel>Año</InputLabel>
            <Select
              label="Año"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {YEARS.map((year, index) => (
                <MenuItem key={index} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div style={{ padding: '10px 0px', textAlign: 'center' }}>
          <CustomButton color="primary" onClick={() => handleSearch()}>
            Buscar
          </CustomButton>
        </div>
      </div>
    </MenuDialog>
  );
};

const AccountingEntries: React.FC = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [data, setData] = useState<AccountingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const [entryTypes, setEntryTypes] = useState<EntryType[]>([]);
  const [nodes, setNodes] = useState<AccountingNode[]>([]);
  const [entryClasses, setEntryClasses] = useState<AccountingEntryClass[]>([]);
  const [accountingPeriods, setAccountingPeriods] = useState<
    AccountingPeriod[]
  >([]);
  const startDateDefault = new Date();
  startDateDefault.setDate(1);
  const endDateDefault = new Date();
  endDateDefault.setMonth(endDateDefault.getMonth() + 1, 1);
  const date = new Date();
  const [startDate, setStartDate] = useState<Date>(startDateDefault);
  const [endDate, setEndDate] = useState<Date>(endDateDefault);
  const [month, setMonth] = useState<number>(date.getMonth() + 1);
  const [year, setYear] = useState<number>(date.getFullYear());
  const [open, setOpen] = useState(false);
  const [entry, setEntry] = useState<AccountingEntry>({
    name: '',
    lines: [],
    date: new Date().toISOString().split('T')[0],
    typeId: 'free',
    classId: '',
    companyId: 0,
    status: 'CLOSED',
  });
  const [successText, setSuccessText] = useState<string | null>(null);
  const [failedText, setFailedText] = useState<string | null>(null);
  const setUp = async (startDate: Date, endDate: Date) => {
    let startDateSearch = startDateDefault;
    if (startDate) startDateSearch = startDate;

    let endDateSearch = endDateDefault;
    if (endDate) endDateSearch = endDate;

    const startDateString = startDateSearch.toISOString().split('T')[0];
    const endDateString = endDateSearch.toISOString().split('T')[0];
    setStartDate(startDateSearch);
    setEndDate(endDateSearch);
    const queryParams = `startDate=${startDateString}&endDate=${endDateString}`;
    try {
      setLoading(true);
      const response = await getAccountingEntries(dispatch, queryParams);
      setData(response);
    } catch (error) {
      console.error('Error al cargar asientos:', error);
    }
    setLoading(false);
  };

  const setUpEntryEditing = async () => {
    try {
      const response = await getAccountingEntryTypes(dispatch);
      const responseNodes = await getAccountingNodes(dispatch);
      const responseEntryClasses = await getAccountingEntryClasses(dispatch);
      const responsePeriods = await getAccountingPeriods(dispatch);

      setEntryTypes(response);
      setNodes(responseNodes);
      setEntryClasses(responseEntryClasses);
      setAccountingPeriods(responsePeriods);
    } catch (error) {
      console.error('Error al cargar datos para editar asientos:', error);
      setFailedText(
        'Error al cargar los datos necesarios para editar asientos.',
      );
    }
  };

  const handleNewEntry = () => {
    setOpen(true);
    setEntry({
      name: '',
      lines: [],
      date: new Date().toISOString().split('T')[0],
      typeId: 'free',
      classId: '',
      companyId: 0,
      status: 'CLOSED',
    });
  };

  const handleEditEntry = (entryId: number) => {
    const entry = data.find((entry: AccountingEntry) => entry.id === entryId);
    if (!entry) return alert('No se encontró la entrada.');

    const dateFormatted = entry.date.split('T')[0];
    setEntry({
      ...entry,
      date: dateFormatted,
    });
    setOpen(true);
  };

  useEffect(() => {
    void setUp(startDateDefault, endDateDefault);
    void setUpEntryEditing();
  }, []);

  if (loading) return <CenteredCircularProgress type="layout" />;
  return (
    <div>
      <div>
        <HeaderWithActions
          title={`Período: ${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`}
          variant="body1"
          item={
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}
            >
              <div>
                <DatesSearcher
                  setUp={setUp}
                  year={year}
                  month={month}
                  setMonth={setMonth}
                  setYear={setYear}
                />
              </div>
              <div>
                <CustomButton
                  color="primary"
                  onClick={() => handleNewEntry()}
                  disabled={
                    !hasAnyPermissions(user, ['accounting_write_entries'])
                  }
                >
                  Crear
                </CustomButton>
              </div>
            </div>
          }
        />
      </div>
      <div style={{ marginTop: 20 }}>
        <AccountingEntriesTable
          entries={data}
          handleEditEntry={handleEditEntry}
        />
      </div>
      {open && (
        <EditEntry
          entry={entry}
          setEntry={setEntry}
          open={open}
          setOpen={setOpen}
          setUp={(startDate?: Date, endDate?: Date) =>
            setUp(startDate!, endDate!)
          }
          setSuccessText={setSuccessText}
          setFailedText={setFailedText}
          entryTypes={entryTypes}
          nodes={nodes}
          entryClasses={entryClasses}
          accountingPeriods={accountingPeriods}
        />
      )}
      <CustomAlert
        open={!!successText}
        onClose={() => setSuccessText(null)}
        severity="success"
        text={successText}
      />
      <CustomAlert
        open={!!failedText}
        onClose={() => setFailedText(null)}
        severity="error"
        text={failedText}
      />
    </div>
  );
};

export default AccountingEntries;
